import {COLORS, SIZES, Typography} from '@fupa/fupa-uikit';
import {AwayGoal, HomeGoal, MatchScore, MatchScoreContainer} from 'app/components/match/MatchDetailHeaderStyles';
import React from 'react';
import {formatDate, formatDayMonthDate, formatTime} from 'app/helpers/dateHelpers';
import MatchLiveMinute from 'app/components/match/MatchLiveMinute';
import styled from 'styled-components';

const MatchKickoff = styled(MatchScore)`
  &&& {
    letter-spacing: normal;
  }
`;

const MatchStatus = styled(Typography)`
  &&& {
    color: ${COLORS.black};
  }
`;

const MatchScoreInformation = styled('div')`
  min-height: ${SIZES['24']};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  text-align: center;
  max-width: 6.25rem;
`;

const MatchFlags = {
  Cancelled: 'cancelled',
  Aborted: 'aborted',
  Verdict: 'verdict',
  Penalty: 'penalty',
  Overtime: 'overtime',
  Ticker: 'ticker',
  SoftTicker: 'softticker',
} as const;

type SofttickerReporter = {
  id: number;
  firstName: string;
  name: string;
};

const getSofttickerName = (reporter: SofttickerReporter) => {
  return reporter?.firstName ?? reporter?.name ?? 'Unbekannt';
};

const getMatchInformation = (match: any) => {
  if (match.flags?.includes(MatchFlags.SoftTicker) && match.live) {
    const additionalMinute = match.live.additionalMinute ? ` +${match.live.additionalMinute}` : '';

    return (
      <MatchScoreInformation>
        <Typography variant='caption1'>{`${match.live.minute}'${additionalMinute}`}</Typography>
        <Typography variant='caption1'>gemeldet von {getSofttickerName(match.lastSofttickerReporter)}</Typography>
      </MatchScoreInformation>
    );
  }

  const labelByFlag = {
    [MatchFlags.Aborted]: 'abgebrochen',
    [MatchFlags.Verdict]: match.verdict ?? 'Urteil',
    [MatchFlags.Penalty]: 'n.E.',
    [MatchFlags.Overtime]: 'n.V.',
  };
  const foundFlag: string | undefined = match.flags?.find((flag: keyof typeof MatchFlags) => labelByFlag[flag]);

  if (!foundFlag) return null;

  return (
    <MatchScoreInformation>
      <Typography variant='caption1'>{labelByFlag[foundFlag]}</Typography>
    </MatchScoreInformation>
  );
};

const SIX_DAYS_IN_MS = 518400000;

const formatKickoff = (kickoff: string): string => {
  const curDate = new Date();
  const kickoffDate = new Date(kickoff);

  const isToday =
    curDate.getFullYear() === kickoffDate.getFullYear() &&
    curDate.getMonth() === kickoffDate.getMonth() &&
    curDate.getDate() === kickoffDate.getDate();

  const isInNext5Days = kickoffDate.getTime() - curDate.getTime() < SIX_DAYS_IN_MS;

  if (isToday) {
    return formatTime(kickoff);
  } else if (isInNext5Days) {
    // Spiel ist in den nächsten 5 Tagen (Wenn heute Mittwoch -> Spiel am Montag)
    return formatDate(kickoff, 'E, HH:mm');
  } else {
    return formatDayMonthDate(kickoff);
  }
};

interface MatchHeaderScoreProps {
  match: any;
}

export default function MatchHeaderScore({match}: MatchHeaderScoreProps) {
  const hasFinalScore = typeof match.homeGoal === 'number' && typeof match.awayGoal === 'number';

  if (match.flags?.includes(MatchFlags.Cancelled)) {
    return (
      <MatchScoreContainer>
        <MatchKickoff>
          <MatchStatus variant='subheading2' component='span'>
            abgesagt
          </MatchStatus>
        </MatchKickoff>
      </MatchScoreContainer>
    );
  }

  const resultInformation = getMatchInformation(match);

  if (match.section === 'LIVE' && match.flags?.includes(MatchFlags.Ticker) && match.live) {
    return <MatchLiveMinute live={match.live} awayGoal={match?.awayGoal} homeGoal={match?.homeGoal} />;
  }

  if (match.section === 'PRE') {
    const hasLiveticker = match.flags?.includes('ticker');
    const kickoff = formatKickoff(match.kickoff);

    return (
      <MatchScoreContainer>
        <MatchKickoff>
          <MatchStatus variant='subheading2' component='span'>
            {kickoff}
          </MatchStatus>
        </MatchKickoff>
        {hasLiveticker ? (
          <MatchScoreInformation>
            <Typography variant='caption1'>live</Typography>
          </MatchScoreInformation>
        ) : null}
      </MatchScoreContainer>
    );
  }

  return (
    <MatchScoreContainer>
      <MatchScore>
        <HomeGoal>
          <Typography variant='display2'>{hasFinalScore ? match.homeGoal : '–'}</Typography>
        </HomeGoal>
        <Typography variant='display2'>:</Typography>
        <AwayGoal>
          <Typography variant='display2'>{hasFinalScore ? match.awayGoal : '–'}</Typography>
        </AwayGoal>
      </MatchScore>
      {resultInformation}
    </MatchScoreContainer>
  );
}
